import * as turf from '@turf/turf';
import shp from 'shpjs';
import {QuicklookPreviewType} from '../types/geoMap';
import {ProductImageStacType} from '../types/product';
import {
  calculateProductImageBearingAngle,
  getCenterPointFeature,
  getLineLength,
  getRotationFeature,
} from './cutSceneHelper';
import {getProductIdentifierFrom} from './productHelper';
import {QuadrilateralCornersType, ShapeFeatureType} from '../types/shape';
import {getCoordinatesFromGeoJSON} from './geoJSONHelper';
import {
  convertToQuadrilateralGeoJSONFrom,
  getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates,
  getQuadrilateralPerimeterSidesFromQuadrilateralCoordinates,
} from './quadrilateralHelper';
import {errorToastNotification} from './toastNotificationHelper';
import {
  getMissionCodesByStacItem
} from './configurationHelper';
import {ConfigurationsType} from '../types/configurations';
import {Position} from '@turf/turf';

const extractGoogleCoords = async (plainText) => {
  let parser = new DOMParser();
  let xmlDoc = parser.parseFromString(plainText, 'text/xml');
  let googlePolygons: any = [];
  let googleMarkers: any = [];

  if (xmlDoc.documentElement.nodeName === 'kml') {
    for (const item of xmlDoc.getElementsByTagName('Placemark') as any) {
      let polygons = item.getElementsByTagName('Polygon');
      let markers = item.getElementsByTagName('Point');

      /** POLYGONS PARSE **/
      for (const polygon of polygons) {
        let coords = polygon.getElementsByTagName('coordinates')[0].childNodes[0].nodeValue.trim();
        let points = coords.split(' ');

        let googlePolygonsPaths: any = [];
        for (const point of points) {
          let coord = point.split(',');
          googlePolygonsPaths.push({lat: +coord[1], lng: +coord[0]});
        }
        googlePolygons.push(googlePolygonsPaths);
      }

      /** MARKER PARSE **/
      for (const marker of markers) {
        var coords = marker.getElementsByTagName('coordinates')[0].childNodes[0].nodeValue.trim();
        let coord = coords.split(',');
        googleMarkers.push({lat: +coord[1], lng: +coord[0]});
      }
    }
  } else {
    throw new Error('error while parsing');
  }

  return {markers: googleMarkers, polygons: googlePolygons};
};

export const parseKMLFile = (file, callback) => {
  const fileReader = new FileReader();
  fileReader.onload = async (e: any) => {
    let result = await extractGoogleCoords(e.target.result);

    //Do something with result object here
    callback(result);
  };
  fileReader.readAsText(file);
};

export const parseShapeFile = (file, callback) => {
  const fileReader = new FileReader();
  fileReader.onload = function (e: any) {
    let _this = this as any;
    if (_this.readyState !== 2 || _this.error) {
      return;
    } else {
      shp(_this.result).then(
        function (geoJson) {
          callback(geoJson);
        },
        function (e) {
          errorToastNotification(`${e}`);
        }
      );
    }
  };
  fileReader.readAsArrayBuffer(file);
};

const getRadiusDistance = (
  corners: QuadrilateralCornersType,
  units?:
    | 'miles'
    | 'nauticalmiles'
    | 'degrees'
    | 'radians'
    | 'inches'
    | 'yards'
    | 'meters'
    | 'metres'
    | 'kilometers'
    | 'kilometres'
    | undefined
) => {
  const leftSideBottom = turf.point(corners.bottomLeft);
  const leftSideUp = turf.point(corners.upLeft);
  const rightSideBottom = turf.point(corners.bottomRight);
  const rightSideUp = turf.point(corners.upRight);
  //left side
  const leftSideDistance = turf.distance(leftSideBottom, leftSideUp, {units});
  //right side
  const rightSideDistance = turf.distance(rightSideBottom, rightSideUp, {units});
  //up side
  const upSideDistance = turf.distance(leftSideUp, rightSideUp, {units});
  //bottom side
  const bottomSideDistance = turf.distance(leftSideBottom, rightSideBottom, {units});
  return Math.max(...[leftSideDistance, rightSideDistance, upSideDistance, bottomSideDistance]);
};

const sanitizeBearingAngle = (bearingAngleDegrees) => {
  let angle = bearingAngleDegrees;
  if (angle > 720 && angle < 1080) {
    angle = angle - 720;
  }
  if (angle > 360 && angle < 720) {
    angle = angle - 360;
  }
  return angle;
};

const getProductBoxInfo = (
  productImageQuadrilateralFeature: ShapeFeatureType,
  isCircle?: boolean
) => {
  const productRotationPoint = getCenterPointFeature(productImageQuadrilateralFeature);
  const productSceneBearingAngle = calculateProductImageBearingAngle(
    productImageQuadrilateralFeature
  );
  const rotatedProductImageQuadrilateralFeature = getRotationFeature(
    productImageQuadrilateralFeature,
    -productSceneBearingAngle,
    productRotationPoint
  );
  const bbox = turf.bbox(rotatedProductImageQuadrilateralFeature);
  let bboxPolygon = turf.bboxPolygon(bbox);
  let orientedBBox = getRotationFeature(
    bboxPolygon,
    productSceneBearingAngle,
    productRotationPoint
  );
  let orientedBBoxCoordinates = getCoordinatesFromGeoJSON(orientedBBox);
  let productCorners = getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(
    orientedBBoxCoordinates
  );
  if (isCircle && productRotationPoint.geometry?.coordinates) {
    const angleDegrees = sanitizeBearingAngle(productSceneBearingAngle * (180 / Math.PI));
    const angleBearingRadians = angleDegrees * (Math.PI / 180);
    const radiousDistance = getRadiusDistance(productCorners, 'miles');
    bboxPolygon = turf.bboxPolygon(
      turf.bbox(
        turf.circle(productRotationPoint.geometry?.coordinates, radiousDistance / 2, {
          steps: 64,
          units: 'miles',
        })
      )
    );
    orientedBBox = getRotationFeature(bboxPolygon, productSceneBearingAngle, productRotationPoint);
    orientedBBoxCoordinates = getCoordinatesFromGeoJSON(orientedBBox);
    productCorners = getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(
      orientedBBoxCoordinates
    );
  }
  return {
    productRotationPoint,
    productSceneBearingAngle,
    productCorners,
    bboxPolygon,
  };
};

export const parseQuicklookPreviewRadar = (
  corners,
  configurations,
  productImage,
  productImageQuadrilateralFeature
) => {
  if (needToRotateQuicklookOf(configurations, productImage)) {
    const rotationPoint = getCenterPointFeature(productImageQuadrilateralFeature);
    const sceneBearingAngle = calculateProductImageBearingAngle(productImageQuadrilateralFeature);
    const rotatedProductImageQuadrilateralFeature = getRotationFeature(
      productImageQuadrilateralFeature,
      -sceneBearingAngle,
      rotationPoint
    );
    const bbox = turf.bbox(rotatedProductImageQuadrilateralFeature);
    const bboxPolygon = turf.bboxPolygon(bbox);
    const orientedBBox = getRotationFeature(bboxPolygon, sceneBearingAngle, rotationPoint);
    const orientedBBoxCoordinates = getCoordinatesFromGeoJSON(orientedBBox);
    corners = getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(orientedBBoxCoordinates);
  } else {
    const bbox = turf.bbox(productImageQuadrilateralFeature);
    const bboxPolygon = turf.bboxPolygon(bbox);
    const orientedBBoxCoordinates = getCoordinatesFromGeoJSON(bboxPolygon);
    corners = getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(orientedBBoxCoordinates);
  }
  return corners;
};

export const parseQuicklookPreviewOptical = (
  corners,
  configurations,
  productImage,
  productImageQuadrilateralFeature,
  productCorners,
  productRotationPoint,
  productSceneBearingAngle
) => {
  if (needToRotateQuicklookOf(configurations, productImage)) {
    if (productRotationPoint.geometry?.coordinates) {
      const productCoords = getCoordinatesFromGeoJSON(productImageQuadrilateralFeature);
      const {left, right} = getQuadrilateralPerimeterSidesFromQuadrilateralCoordinates(
        productCoords
      );
      const line = getLineLength(left) > getLineLength(right);
      const angleDegrees = sanitizeBearingAngle(productSceneBearingAngle * (180 / Math.PI));
      const angleBearingRadians = angleDegrees * (Math.PI / 180);
      const radiousDistance = getRadiusDistance(productCorners, 'miles');
      const getProductCircleInfo = getProductBoxInfo(productImageQuadrilateralFeature, true);
      let fromPoint = getProductCircleInfo.productCorners.bottomRight;
      let toPoint = productCorners.bottomRight;
      if (getLineLength(left) < getLineLength(right)) {
        fromPoint = getProductCircleInfo.productCorners.bottomLeft;
        toPoint = productCorners.bottomLeft;
      }
      const from = turf.point(fromPoint);
      const to = turf.point(toPoint);
      //finds the bearing angle between the points
      const bearing = turf.rhumbBearing(from, to);
      //calculates the distance between the points
      const distance = turf.rhumbDistance(from, to);
      //moves the polygon to the distance on the direction angle.
      const translatedPoly = turf.transformTranslate(
        getProductCircleInfo.bboxPolygon,
        distance,
        bearing
      );
      const newOrientedBBox = getRotationFeature(
        translatedPoly,
        angleBearingRadians,
        productRotationPoint
      );
      const newOrientedBBoxCoordinates = getCoordinatesFromGeoJSON(newOrientedBBox);
      corners = getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(
        newOrientedBBoxCoordinates
      );
      if (productSceneBearingAngle * (180 / Math.PI) > 360 || angleDegrees > 195) {
        const shadowBbox = turf.bbox(productImageQuadrilateralFeature);
        const shadowBboxPolygon = turf.bboxPolygon(shadowBbox);
        const shadowOrientedBBoxCoordinates = getCoordinatesFromGeoJSON(shadowBboxPolygon);
        const shadowBbbcorners = getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(
          shadowOrientedBBoxCoordinates
        );
        const center = getCenterPointFeature(shadowBboxPolygon);
        if (center.geometry?.coordinates) {
          const radiousDistance = getRadiusDistance(shadowBbbcorners, 'miles');
          const bboxPolygon = turf.bboxPolygon(
            turf.bbox(turf.circle(center, radiousDistance / 2, {steps: 64, units: 'miles'}))
          );
          const orientedBBoxCoordinates = getCoordinatesFromGeoJSON(bboxPolygon);
          corners = getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(
            orientedBBoxCoordinates
          );
          corners = movePoints(shadowBbbcorners, corners, bboxPolygon, center);
        }
      }
    }
  } else {
    const bbox = turf.bbox(productImageQuadrilateralFeature);
    const bboxPolygon = turf.bboxPolygon(bbox);
    const orientedBBoxCoordinates = getCoordinatesFromGeoJSON(bboxPolygon);
    corners = getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(orientedBBoxCoordinates);
  }
  return corners;
};

export const parseQuicklookPreviewFrom = (
  configurations: ConfigurationsType,
  productImage: ProductImageStacType,
  rawImage: Blob | null = null,
  isShownInMap = false
): QuicklookPreviewType => {
  let corners: QuadrilateralCornersType = {
    upLeft: [0, 0],
    upRight: [0, 0],
    bottomLeft: [0, 0],
    bottomRight: [0, 0],
  };

  try {
    const productImageQuadrilateralFeature = convertToQuadrilateralGeoJSONFrom(
      productImage as ShapeFeatureType
    ) as ShapeFeatureType;
    const {productRotationPoint, productSceneBearingAngle, productCorners} = getProductBoxInfo(
      productImageQuadrilateralFeature
    );
    corners = parseQuicklookPreviewOptical(
      corners,
      configurations,
      productImage,
      productImageQuadrilateralFeature,
      productCorners,
      productRotationPoint,
      productSceneBearingAngle
    );
    if (true) { // @mapitaly: sempre vero configurations.productName === '003'
      corners = parseQuicklookPreviewRadar(
        corners,
        configurations,
        productImage,
        productImageQuadrilateralFeature
      );
    }
  } catch (e) {
    console.warn(`Quicklook: ${e.message}`, productImage);
    isShownInMap = false;
  }

  return {
    productImageId: getProductIdentifierFrom(productImage),
    url: (rawImage && URL.createObjectURL(rawImage)) || null,
    corners,
    isShownInMap,
  };
};

const movePoints = (shadowCorners, corners, bboxPolygon, rotationPoint) => {
  let fromPoint = corners.upLeft;
  let toPoint = shadowCorners.upLeft;
  const from = turf.point(fromPoint);
  const to = turf.point(toPoint);
  //finds the bearing angle between the points
  const bearing = turf.rhumbBearing(from, to);
  //calculates the distance between the points
  const distance = turf.rhumbDistance(from, to);
  //moves the polygon to the distance on the direction angle.
  const translatedPoly = turf.transformTranslate(bboxPolygon, distance, bearing);
  const newOrientedBBox = getRotationFeature(translatedPoly, bearing, rotationPoint);
  const newOrientedBBoxCoordinates = getCoordinatesFromGeoJSON(translatedPoly);
  return getQuadrilateralPerimeterCornersFromQuadrilateralCoordinates(newOrientedBBoxCoordinates);
};

const needToRotateQuicklookOf = (
  configurations: ConfigurationsType,
  productImage: ProductImageStacType
) => true;
